import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  X,
  ArrowRight,
  ArrowLeft,
  Target,
  Users,
  Building,
  Lightbulb,
  Compass,
  Database,
  GitBranch,
  Settings,
} from 'lucide-react';

interface ProductTourProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface TourStep {
  id: string;
  title: string;
  subtitle: string;
  content: React.ReactNode;
  targetPath: string;
  highlightElement?: string;
  explanation: string;
}

export const ProductTour: React.FC<ProductTourProps> = ({ isOpen, onClose, onComplete }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const wasOpenRef = useRef(false);

  const tourSteps: TourStep[] = [
    {
      id: 'outcome-driver',
      title: 'The Outcome Driver',
      subtitle: 'Why we\'re building this in the first place',
      // Mission Control; /home is now the team hub (Proposal 09 § 8.2).
      targetPath: '/elevation/home',
      explanation: 'Let me start by showing you the fundamental problem we\'re solving and why it matters.',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-coral-50 to-amber-50 p-4 border border-coral-200">
            <h3 className="font-semibold text-charcoal-900 mb-2">The Problem We\'re Solving</h3>
            <p className="text-sm text-charcoal-700">
              Most organizations struggle with scattered focus, colliding decisions, and unclear tradeoffs. 
              People work hard but lack the systematic approach to prioritization that creates real impact.
            </p>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-coral-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-coral-600 font-bold text-xs">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">Current State</h4>
                <p className="text-xs text-charcoal-600">Strategy documents exist but weekly priorities change mid-stream</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[var(--mustard-dim)] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[var(--mustard)] font-bold text-xs">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">Problem/Opportunity</h4>
                <p className="text-xs text-charcoal-600">Scattered focus, colliding decisions, firefighting overrides learning</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-primary-600 font-bold text-xs">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">Impact</h4>
                <p className="text-xs text-charcoal-600">Financial, strategic, cultural, and personal energy consequences</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'who-for',
      title: 'Who We\'re Building For',
      subtitle: 'The three audiences that drive our approach',
      targetPath: '/about',
      explanation: 'Now let me show you who we\'re serving and how that shapes everything we build.',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-primary-50 to-sage-50 p-4 border border-primary-200">
            <h3 className="font-semibold text-charcoal-900 mb-2">Our Three Audiences</h3>
            <p className="text-sm text-charcoal-700">
              Every decision we make is driven by serving these three groups and their unique needs.
            </p>
          </div>
          
          <div className="space-y-3">
            <div className="bg-[var(--bg-raised)] p-3 border border-charcoal-200">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-primary-600" />
                <h4 className="font-semibold text-charcoal-900 text-sm">Individuals</h4>
              </div>
              <p className="text-xs text-charcoal-600">Personal productivity, clarity, and systematic approach to priorities</p>
            </div>
            
            <div className="bg-[var(--bg-raised)] p-3 border border-charcoal-200">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-sage-600" />
                <h4 className="font-semibold text-charcoal-900 text-sm">Teams</h4>
              </div>
              <p className="text-xs text-charcoal-600">Alignment, shared language, and collaborative decision-making</p>
            </div>
            
            <div className="bg-[var(--bg-raised)] p-3 border border-charcoal-200">
              <div className="flex items-center gap-2 mb-2">
                <Building className="h-4 w-4 text-[var(--mustard)]" />
                <h4 className="font-semibold text-charcoal-900 text-sm">Organizations</h4>
              </div>
              <p className="text-xs text-charcoal-600">Capability development, cultural transformation, and systematic excellence</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'why-important',
      title: 'Why This Matters Now',
      subtitle: 'The business case and market opportunity',
      targetPath: '/engines',
      explanation: 'Let me show you the engines that power this system and why the timing is right.',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-sage-50 to-primary-50 p-4 border border-sage-200">
            <h3 className="font-semibold text-charcoal-900 mb-2">Market Timing & Opportunity</h3>
            <p className="text-sm text-charcoal-700">
              The market is ready for a systematic approach to prioritization that scales from individual to organizational impact.
            </p>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-sage-600 font-bold text-xs">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">Market Gap</h4>
                <p className="text-xs text-charcoal-600">Existing tools are either too simple or too complex, nothing scales systematically</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-primary-600 font-bold text-xs">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">Technology Ready</h4>
                <p className="text-xs text-charcoal-600">AI, content graphs, and modern web tech enable personalized, scalable solutions</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[var(--mustard-dim)] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[var(--mustard)] font-bold text-xs">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">Business Model</h4>
                <p className="text-xs text-charcoal-600">Individual → Team → Organization creates compound value and recurring revenue</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'how-works-mof',
      title: 'How It Works: MOF Architecture',
      subtitle: 'The four-layer system that makes it all possible',
      targetPath: '/grammar',
      explanation: 'Let me show you the technical architecture that enables this systematic approach.',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-charcoal-50 to-primary-50 p-4 border border-charcoal-200">
            <h3 className="font-semibold text-charcoal-900 mb-2">MOF: Meta-Object Facility</h3>
            <p className="text-sm text-charcoal-700">
              A four-layer architecture that creates a self-documenting, versionable, and scalable knowledge system.
            </p>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-2 bg-primary-50 border border-primary-200">
              <Database className="h-4 w-4 text-primary-600" />
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">M3: Metadata</h4>
                <p className="text-xs text-charcoal-600">Defines the structure and relationships</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-2 bg-sage-50 border border-sage-200">
              <Lightbulb className="h-4 w-4 text-sage-600" />
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">M2: Models</h4>
                <p className="text-xs text-charcoal-600">Provides templates and frameworks</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-2 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
              <GitBranch className="h-4 w-4 text-[var(--mustard)]" />
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">M1: Templates</h4>
                <p className="text-xs text-charcoal-600">Creates specific instances and applications</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-2 bg-coral-50 border border-coral-200">
              <Settings className="h-4 w-4 text-coral-600" />
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">M0: Runtime</h4>
                <p className="text-xs text-charcoal-600">Executes and delivers the experience</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'how-works-engines',
      title: 'How It Works: The Engines',
      subtitle: 'The infrastructure that powers personalized experiences',
      targetPath: '/engines',
      explanation: 'Now let me show you the engines that make this system intelligent and personalized.',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-primary-50 to-sage-50 p-4 border border-primary-200">
            <h3 className="font-semibold text-charcoal-900 mb-2">The Engine Pipeline</h3>
            <p className="text-sm text-charcoal-700">
              Seven specialized engines work together to create personalized, intelligent experiences.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-[var(--bg-raised)] p-2 rounded border border-charcoal-200">
              <h4 className="font-semibold text-charcoal-900">Capture</h4>
              <p className="text-charcoal-600">Collects user input and context</p>
            </div>
            <div className="bg-[var(--bg-raised)] p-2 rounded border border-charcoal-200">
              <h4 className="font-semibold text-charcoal-900">Classify</h4>
              <p className="text-charcoal-600">Maps to our taxonomy</p>
            </div>
            <div className="bg-[var(--bg-raised)] p-2 rounded border border-charcoal-200">
              <h4 className="font-semibold text-charcoal-900">Relate</h4>
              <p className="text-charcoal-600">Finds relevant content</p>
            </div>
            <div className="bg-[var(--bg-raised)] p-2 rounded border border-charcoal-200">
              <h4 className="font-semibold text-charcoal-900">Compose</h4>
              <p className="text-charcoal-600">Assembles experiences</p>
            </div>
            <div className="bg-[var(--bg-raised)] p-2 rounded border border-charcoal-200">
              <h4 className="font-semibold text-charcoal-900">Deliver</h4>
              <p className="text-charcoal-600">Renders and serves</p>
            </div>
            <div className="bg-[var(--bg-raised)] p-2 rounded border border-charcoal-200">
              <h4 className="font-semibold text-charcoal-900">Measure</h4>
              <p className="text-charcoal-600">Tracks outcomes</p>
            </div>
            <div className="bg-[var(--bg-raised)] p-2 rounded border border-charcoal-200 col-span-2">
              <h4 className="font-semibold text-charcoal-900">Refine</h4>
              <p className="text-charcoal-600">Improves based on feedback and learning</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'content-graph',
      title: 'The Content Graph',
      subtitle: 'How we create intelligent, personalized experiences',
      targetPath: '/models',
      explanation: 'Let me show you how the content graph enables intelligent recommendations and personalized experiences.',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-sage-50 to-amber-50 p-4 border border-sage-200">
            <h3 className="font-semibold text-charcoal-900 mb-2">Rich Metadata + Relationships</h3>
            <p className="text-sm text-charcoal-700">
              Every piece of content has rich metadata that describes its purpose, audience, and relationships to other content.
            </p>
          </div>
          
          <div className="space-y-3">
            <div className="bg-[var(--bg-raised)] p-3 border border-charcoal-200">
              <h4 className="font-semibold text-charcoal-900 text-sm mb-2">Content Metadata</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>• Audience (individual/team/org)</div>
                <div>• Capability family</div>
                <div>• Maturity level</div>
                <div>• Time to consume</div>
                <div>• Prerequisites</div>
                <div>• Related content</div>
              </div>
            </div>
            
            <div className="bg-[var(--bg-raised)] p-3 border border-charcoal-200">
              <h4 className="font-semibold text-charcoal-900 text-sm mb-2">Intelligent Recommendations</h4>
              <p className="text-xs text-charcoal-600">
                The system uses this metadata to surface the right content at the right time for each user's context and goals.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'business-model',
      title: 'The Business Model',
      subtitle: 'How we create compound value and recurring revenue',
      targetPath: '/lab',
      explanation: 'Finally, let me show you how this all comes together to create a sustainable, scalable business.',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-amber-50 to-coral-50 p-4 border border-[var(--mustard)]">
            <h3 className="font-semibold text-charcoal-900 mb-2">Compound Value Creation</h3>
            <p className="text-sm text-charcoal-700">
              Each layer creates value and enables the next, creating a natural progression and compound returns.
            </p>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-primary-600 font-bold text-xs">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">Individual Value</h4>
                <p className="text-xs text-charcoal-600">Personal productivity and clarity tools create immediate value</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-sage-600 font-bold text-xs">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">Team Value</h4>
                <p className="text-xs text-charcoal-600">Alignment and collaboration tools multiply individual impact</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[var(--mustard-dim)] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[var(--mustard)] font-bold text-xs">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900 text-sm">Organizational Value</h4>
                <p className="text-xs text-charcoal-600">Capability development and cultural transformation create enterprise value</p>
              </div>
            </div>
          </div>
          
          <div className="bg-primary-50 border border-primary-200 p-3">
            <p className="text-primary-800 text-xs">
              <strong>Key Insight:</strong> This creates a natural upgrade path and compound value that justifies premium pricing at each level.
            </p>
          </div>
        </div>
      )
    }
  ];

  const currentStepData = tourSteps[currentStep];

  // Reset to step 1 each time the tour opens.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setCurrentStep(0);
      setIsNavigating(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  // Navigate to the target path when the active step changes.
  useEffect(() => {
    if (!isOpen || !currentStepData) return;

    if (location.pathname === currentStepData.targetPath) {
      setIsNavigating(false);
      return;
    }

    setIsNavigating(true);
    navigate(currentStepData.targetPath);
  }, [currentStep, isOpen, navigate, location.pathname, currentStepData]);

  const goToNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const goToPrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-charcoal-900/50 backdrop-blur-sm"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        />
        
        <div
          className="relative bg-[var(--bg-raised)] shadow-2xl border border-charcoal-200 w-full max-w-4xl max-h-[90vh] overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-charcoal-200">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary-100 flex items-center justify-center">
                <Compass className="h-4 w-4 text-primary-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-charcoal-900">{currentStepData.title}</h2>
                <p className="text-sm text-charcoal-600">{currentStepData.subtitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-charcoal-500 hover:text-charcoal-700 hover:bg-charcoal-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="px-6 py-3 bg-charcoal-50 border-b border-charcoal-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-charcoal-700">
                Step {currentStep + 1} of {tourSteps.length}
              </span>
              <span className="text-sm text-charcoal-600">
                {Math.round(((currentStep + 1) / tourSteps.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-charcoal-200 rounded-full h-2">
              <div 
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
              ></div>
            </div>
            
            {/* Step Navigation */}
            <div className="flex gap-1 mt-3">
              {tourSteps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToStep(index)}
                  className={`h-2 flex-1 rounded-full transition-colors ${
                    index === currentStep 
                      ? 'bg-primary-600' 
                      : index < currentStep 
                        ? 'bg-primary-300' 
                        : 'bg-charcoal-200'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {isNavigating ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-3 text-charcoal-600">Navigating...</span>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-[var(--mustard-dim)] p-4 border border-[var(--mustard)]">
                  <p className="text-sm text-charcoal-700 italic">
                    "{currentStepData.explanation}"
                  </p>
                </div>
                
                {currentStepData.content}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-charcoal-200 bg-charcoal-50">
            <div className="flex items-center gap-4">
              <button
                onClick={goToPrevious}
                disabled={currentStep === 0}
                className="inline-flex items-center gap-2 px-4 py-2 text-charcoal-600 hover:text-charcoal-900 disabled:text-charcoal-400 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Previous
              </button>
              
              <button
                onClick={onClose}
                className="text-sm text-charcoal-500 hover:text-charcoal-700 transition-colors"
              >
                Skip Tour
              </button>
            </div>

            <button
              onClick={goToNext}
              className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 font-semibold transition-colors"
            >
              {currentStep === tourSteps.length - 1 ? 'Complete Tour' : 'Next'}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
